import React, { useState } from "react";
import PaginatedCardList from "../pagination/PaginatedCardList";

const VIDEOS_PER_PAGE = 4;
const FALLBACK_THUMBNAIL = "/dist/img/game-video-thumbnail.png";

function getVideoThumbnail(playbackUrl) {
    const videoId = playbackUrl?.match(/youtube\.com\/embed\/([^?&#/]+)/)?.[1];
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : FALLBACK_THUMBNAIL;
}

export default function GameVideoPlayer({
    videos = [],
    game,
    latestGameEvent,
    eventTitle,
    accent,
    t,
    language,
}) {
    const [selectedVideoId, setSelectedVideoId] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoPage, setVideoPage] = useState(1);

    const mostRecentVideo = [...videos].sort((first, second) => (
        new Date(second.createdAt || 0).getTime() - new Date(first.createdAt || 0).getTime()
    ))[0];
    const featuredVideo = videos.find((video) => String(video.id) === String(selectedVideoId))
        || mostRecentVideo
        || null;
    const otherVideos = videos.filter((video) => String(video.id) !== String(featuredVideo?.id));
    const totalPages = Math.max(1, Math.ceil(otherVideos.length / VIDEOS_PER_PAGE));
    const currentPage = Math.min(videoPage, totalPages);
    const pageVideos = otherVideos.slice(
        (currentPage - 1) * VIDEOS_PER_PAGE,
        currentPage * VIDEOS_PER_PAGE,
    );

    if (!featuredVideo?.playbackUrl || !game) return null;

    const selectVideo = (video) => {
        setSelectedVideoId(video.id);
        setIsPlaying(true);
        setVideoPage(1);
    };

    const renderVideoCard = (video) => {
        const isLive = video.status === "live";
        const recordedAt = video.createdAt
            ? new Date(video.createdAt).toLocaleString(language)
            : "";

        return (
            <div key={video.id} className="col-12">
                <button
                    type="button"
                    onClick={() => selectVideo(video)}
                    aria-label={`${t("publicEventPage.liveStream.playVideo", {}, "Play video")}: ${video.title || eventTitle}`}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 11,
                        width: "100%",
                        padding: 8,
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 11,
                        color: "#fff",
                        background: "rgba(255,255,255,0.035)",
                        textAlign: "left",
                        cursor: "pointer",
                        transition: "background 140ms ease, border-color 140ms ease",
                    }}
                    onMouseEnter={(event) => {
                        event.currentTarget.style.background = "rgba(255,255,255,0.08)";
                        event.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                    }}
                    onMouseLeave={(event) => {
                        event.currentTarget.style.background = "rgba(255,255,255,0.035)";
                        event.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                    }}
                >
                    <span style={{ position: "relative", display: "block", width: 112, height: 63, flexShrink: 0, overflow: "hidden", borderRadius: 7, background: "#07182a" }}>
                        <img
                            src={getVideoThumbnail(video.playbackUrl)}
                            alt=""
                            onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = FALLBACK_THUMBNAIL;
                            }}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                        <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#fff", fontSize: 17, textShadow: "0 1px 5px rgba(0,0,0,.8)" }}>▶</span>
                        {isLive ? (
                            <span style={{ position: "absolute", top: 4, left: 4, padding: "3px 6px", borderRadius: 4, background: "#e5394f", color: "#fff", fontSize: 8, lineHeight: 1.2, fontWeight: 900, letterSpacing: ".06em" }}>
                                LIVE
                            </span>
                        ) : null}
                    </span>
                    <span style={{ display: "flex", minWidth: 0, flex: 1, flexDirection: "column", gap: 4 }}>
                        <span style={{ overflow: "hidden", color: "#f4f7fb", fontSize: 12, fontWeight: 700, lineHeight: 1.35, textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {video.title || eventTitle || t("publicEventPage.liveStream.title", {}, "Game video")}
                        </span>
                        <span style={{ color: "#9da8b7", fontSize: 10 }}>
                            {isLive ? t("publicEventPage.liveStream.live", {}, "Live") : recordedAt}
                        </span>
                    </span>
                </button>
            </div>
        );
    };

    return (
        <div>
            <section
                aria-label={t("publicEventPage.liveStream.title", {}, "Game videos")}
                className="kbs-card mb-3"
                style={{ borderRadius: 18, overflow: "hidden", backgroundColor: "#05070a", boxShadow: "0 20px 55px rgba(0,0,0,0.55)" }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 14px", color: "#fff", background: "#11141a" }}>
                    <span style={{ overflow: "hidden", fontSize: 13, fontWeight: 700, textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {featuredVideo.title || eventTitle || t("publicEventPage.liveStream.title", {}, "Game videos")}
                    </span>
                    {featuredVideo.status === "live" ? (
                        <span style={{ flexShrink: 0, padding: "4px 8px", borderRadius: 999, background: "rgba(229,57,79,.16)", color: "#ff6675", fontSize: 9, fontWeight: 900, letterSpacing: ".08em" }}>
                            LIVE
                        </span>
                    ) : (
                        <span style={{ flexShrink: 0, color: "#9da8b7", fontSize: 10, fontWeight: 700 }}>
                            {featuredVideo.createdAt ? new Date(featuredVideo.createdAt).toLocaleString(language) : t("publicEventPage.liveStream.recorded", {}, "Recording")}
                        </span>
                    )}
                </div>
                <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#062246" }}>
                    {isPlaying ? (
                        <iframe
                            title={featuredVideo.title || eventTitle}
                            src={featuredVideo.playbackUrl}
                            allow="autoplay; encrypted-media; picture-in-picture"
                            allowFullScreen
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                        />
                    ) : (
                        <button
                            type="button"
                            aria-label={t("publicEventPage.liveStream.playVideo", {}, "Play video")}
                            onClick={() => setIsPlaying(true)}
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", padding: 0, border: 0, cursor: "pointer", background: "#062246" }}
                        >
                            <img src={FALLBACK_THUMBNAIL} alt={t("publicEventPage.liveStream.thumbnailAlt", {}, "Game video thumbnail")} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                            <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,0.2)" }}>
                                <span style={{ width: 62, height: 62, borderRadius: "50%", display: "grid", placeItems: "center", paddingLeft: 4, background: "rgba(255,79,94,0.94)", color: "#fff", fontSize: 25, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>▶</span>
                            </span>
                        </button>
                    )}
                    {featuredVideo.status === "live" ? (
                        <div style={{ position: "absolute", zIndex: 1, top: 14, left: 14, display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, background: "rgba(5, 7, 10, 0.86)", color: "#fff", pointerEvents: "none", boxShadow: "0 3px 14px rgba(0,0,0,.3)" }}>
                            <span style={{ color: "#ff4f5e", fontSize: 11, fontWeight: 800, letterSpacing: ".08em" }}>LIVE</span>
                            <span style={{ fontWeight: 700, fontSize: 14 }}>
                                {game.visitorTeam?.name || game.visitorTeamAlias} {game.scoreVisitor ?? 0}
                                <span style={{ opacity: 0.65, margin: "0 5px" }}>–</span>
                                {game.scoreHome ?? 0} {game.homeTeam?.name || game.homeTeamAlias}
                            </span>
                        </div>
                    ) : null}
                    {featuredVideo.status === "live" && latestGameEvent ? (
                        <div aria-live="polite" style={{ position: "absolute", zIndex: 1, bottom: 14, left: 14, maxWidth: "calc(100% - 28px)", padding: "9px 12px", borderRadius: 10, background: "rgba(5, 7, 10, 0.88)", color: "#fff", pointerEvents: "none", boxShadow: "0 3px 14px rgba(0,0,0,.3)" }}>
                            <span style={{ color: accent, fontSize: 10, fontWeight: 800, letterSpacing: ".08em", marginRight: 8 }}>
                                {latestGameEvent.actionType || t("publicEventPage.timeline.title")}
                            </span>
                            <span style={{ fontWeight: 700, fontSize: 13 }}>
                                {latestGameEvent.player?.fullName || latestGameEvent.team?.name || ""}
                                {latestGameEvent.minute != null ? ` · ${latestGameEvent.minute}'` : ""}
                            </span>
                        </div>
                    ) : null}
                </div>
            </section>

            {otherVideos.length > 0 ? (
                <PaginatedCardList
                    title={t("publicEventPage.liveStream.moreVideos", {}, "More videos")}
                    subtitle={t("publicEventPage.liveStream.videoLibrary", {}, "Live streams and recordings")}
                    variant="dark"
                    items={pageVideos}
                    renderItem={renderVideoCard}
                    page={currentPage}
                    pages={totalPages}
                    onPageChange={setVideoPage}
                    loading={false}
                    loadingOverlay={false}
                />
            ) : null}
        </div>
    );
}
